import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CELL_SIZE = 13;
const CELL_GAP = 2;
const CELL_STEP = CELL_SIZE + CELL_GAP;
const MARGIN = { top: 30, right: 16, bottom: 16, left: 36 };
const MONTH_LABEL_HEIGHT = 16;
const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', 'Sun'];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function CalendarHeatmap({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Build a lookup map from date string to data entry
    const dataMap = new Map();
    data.forEach((d) => {
      dataMap.set(d.date, d);
    });

    // Determine date range
    const allDates = data.map((d) => new Date(d.date + 'T00:00:00'));
    const minDate = d3.min(allDates);
    const maxDate = d3.max(allDates);

    // Extend to full weeks: start on Sunday of the first week, end on Saturday of the last week
    const startDate = d3.timeWeek.floor(minDate);
    const endDate = d3.timeDay.offset(d3.timeWeek.ceil(maxDate), -1);

    // Generate all days in range
    const days = d3.timeDays(startDate, d3.timeDay.offset(endDate, 1));

    // Color scale based on changeCount / totalAddresses ratio
    const colorScale = d3.scaleThreshold()
      .domain([0.001, 0.1, 0.3, 0.6])
      .range(['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39']);

    // Calculate weeks (columns) — each week starts on Sunday
    const weekOf = (date) => d3.timeWeek.count(startDate, date);
    const numWeeks = weekOf(days[days.length - 1]) + 1;

    const width = MARGIN.left + numWeeks * CELL_STEP + MARGIN.right;
    const height = MARGIN.top + MONTH_LABEL_HEIGHT + 7 * CELL_STEP + MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g');

    // ---------------------------------------------------------------
    // Day-of-week labels (left side)
    // ---------------------------------------------------------------
    const gridTop = MARGIN.top + MONTH_LABEL_HEIGHT;
    DAY_LABELS.forEach((label, i) => {
      if (!label) return;
      g.append('text')
        .attr('x', MARGIN.left - 6)
        .attr('y', gridTop + i * CELL_STEP + CELL_SIZE / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '9px')
        .style('fill', '#6b7280')
        .text(label);
    });

    // ---------------------------------------------------------------
    // Month labels (top)
    // ---------------------------------------------------------------
    const months = d3.timeMonths(
      d3.timeMonth.floor(startDate),
      d3.timeMonth.offset(d3.timeMonth.floor(endDate), 1)
    );
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    months.forEach((monthStart) => {
      // Find the first day of this month that falls within our range
      const firstDayInMonth = days.find(
        (d) => d.getMonth() === monthStart.getMonth() && d.getFullYear() === monthStart.getFullYear()
      );
      if (!firstDayInMonth) return;

      const weekIndex = weekOf(firstDayInMonth);
      g.append('text')
        .attr('x', MARGIN.left + weekIndex * CELL_STEP)
        .attr('y', MARGIN.top + MONTH_LABEL_HEIGHT - 4)
        .attr('text-anchor', 'start')
        .style('font-size', '10px')
        .style('fill', '#6b7280')
        .text(monthNames[monthStart.getMonth()]);
    });

    // ---------------------------------------------------------------
    // Day cells
    // ---------------------------------------------------------------
    days.forEach((day) => {
      const dateStr = d3.timeFormat('%Y-%m-%d')(day);
      const entry = dataMap.get(dateStr);
      const changeCount = entry ? entry.changeCount : 0;
      const totalAddresses = entry ? entry.totalAddresses : 0;
      const ratio = totalAddresses > 0 ? changeCount / totalAddresses : 0;

      const col = weekOf(day);
      const row = day.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
      // Remap: Mon=0, Tue=1, ..., Sun=6
      const rowMapped = row === 0 ? 6 : row - 1;

      const x = MARGIN.left + col * CELL_STEP;
      const y = gridTop + rowMapped * CELL_STEP;

      g.append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', CELL_SIZE)
        .attr('height', CELL_SIZE)
        .attr('fill', colorScale(ratio))
        .attr('rx', 2)
        .attr('stroke', '#d0d7de')
        .attr('stroke-width', 0.5)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          const pct = totalAddresses > 0
            ? ((changeCount / totalAddresses) * 100).toFixed(1)
            : '0.0';
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: dateStr,
            changeCount,
            totalAddresses,
            percentage: pct,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Change Calendar</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No calendar data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Change Calendar</div>
      <div style={styles.scrollContainer}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '2px', color: '#1f2937' }}>
            {tooltip.date}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            {tooltip.changeCount} address{tooltip.changeCount !== 1 ? 'es' : ''} changed
          </div>
          <div style={{ color: '#2563eb', fontWeight: 600 }}>
            {tooltip.percentage}% of addresses
          </div>
        </div>
      )}
    </div>
  );
}
